import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import './Auth.css';

// Esquema de validación
const RegisterSchema = Yup.object().shape({
  name: Yup.string()
    .required('Nombre es obligatorio')
    .min(3, 'Nombre demasiado corto'),
  email: Yup.string()
    .email('Email inválido')
    .required('Email es obligatorio'),
  password: Yup.string()
    .required('Contraseña es obligatoria')
    .min(6, 'La contraseña debe tener al menos 6 caracteres'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Las contraseñas deben coincidir')
    .required('Confirma tu contraseña')
});

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [apiError, setApiError] = useState('');
  const [debugInfo, setDebugInfo] = useState(null);
  
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Quitar confirmPassword antes de enviar al servidor
      const { confirmPassword, ...userData } = values;
      
      // Depuración: Mostrar lo que se está enviando
      console.log('Enviando datos de registro:', userData);
      
      // Intentar registrar
      const response = await register(userData);
      console.log('Respuesta de registro exitoso:', response);
      
      // Navegar al dashboard si todo sale bien
      navigate('/dashboard');
    } catch (error) {
      console.error('Error completo de registro:', error);
      
      // Mostrar información de depuración
      setDebugInfo({
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        stack: error.stack
      });
      
      // Mostrar mensaje amigable de error
      setApiError(error.response?.data?.message || 'Error al registrar usuario. Por favor, intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>Crear Cuenta</h2>
        
        {apiError && (
          <div className="alert alert-danger" role="alert">
            {apiError}
          </div>
        )}
        
        <Formik
          initialValues={{ name: '', email: '', password: '', confirmPassword: '' }}
          validationSchema={RegisterSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form>
              <div className="mb-3">
                <label htmlFor="name" className="form-label">Nombre</label>
                <Field
                  type="text"
                  name="name"
                  className={`form-control ${errors.name && touched.name ? 'is-invalid' : ''}`}
                />
                <ErrorMessage name="name" component="div" className="invalid-feedback" />
              </div>
              
              <div className="mb-3">
                <label htmlFor="email" className="form-label">Email</label>
                <Field
                  type="email"
                  name="email"
                  className={`form-control ${errors.email && touched.email ? 'is-invalid' : ''}`}
                />
                <ErrorMessage name="email" component="div" className="invalid-feedback" />
              </div>
              
              <div className="mb-3">
                <label htmlFor="password" className="form-label">Contraseña</label>
                <Field
                  type="password"
                  name="password"
                  className={`form-control ${errors.password && touched.password ? 'is-invalid' : ''}`}
                />
                <ErrorMessage name="password" component="div" className="invalid-feedback" />
              </div>
              
              <div className="mb-3">
                <label htmlFor="confirmPassword" className="form-label">Confirmar Contraseña</label>
                <Field
                  type="password"
                  name="confirmPassword"
                  className={`form-control ${errors.confirmPassword && touched.confirmPassword ? 'is-invalid' : ''}`}
                />
                <ErrorMessage name="confirmPassword" component="div" className="invalid-feedback" />
              </div>
              
              <button 
                type="submit" 
                className="btn btn-primary w-100" 
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Registrando...' : 'Registrarse'}
              </button>
            </Form>
          )}
        </Formik>
        
        <div className="mt-3 text-center">
          ¿Ya tienes una cuenta? <Link to="/login">Iniciar Sesión</Link>
        </div>
        
        {/* Sección de depuración (solo visible durante el desarrollo) */}
        {debugInfo && process.env.NODE_ENV !== 'production' && (
          <div className="mt-4 border p-3 bg-light">
            <h5>Información de depuración:</h5>
            <pre className="text-danger" style={{fontSize: '12px', overflow: 'auto', maxHeight: '200px'}}>
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
            <button 
              className="btn btn-sm btn-secondary mt-2"
              onClick={() => setDebugInfo(null)}
            >
              Ocultar detalles
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;
